import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9nisie8mbnljin5a1g0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const errorMsg = serverMsg.includes('spam') || serverMsg.includes('form data is spam')
          ? '提交失败，请稍后重试'
          : (typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormError(errorMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '68影院',
    url: 'https://www.68cinema.com',
    description: '68影院是专业的在线电影内容平台，提供最新最全的高清电影资源',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutData.contact.phone,
      email: aboutData.contact.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutData.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-12 md:pb-16 bg-background-50">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="text-center py-10 md:py-14">
            <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-900 mb-4">
              关于<span className="text-primary-500">68影院</span>
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-3xl mx-auto leading-relaxed">
              {aboutData.brandIntro}
            </p>
          </section>

          {/* Stats */}
          <section className="py-8 mb-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutData.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-10 md:py-12 border-t border-background-200/70">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6 text-center">
                品牌<span className="text-primary-500">故事</span>
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {aboutData.brandStory}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div className="p-6 rounded-lg bg-primary-50 border border-primary-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-focus-3-line text-lg"></i>
                  </div>
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">我们的使命</h3>
                  <p className="text-sm text-foreground-600">{aboutData.mission}</p>
                </div>
                <div className="p-6 rounded-lg bg-accent-50 border border-accent-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-eye-line text-lg"></i>
                  </div>
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">我们的愿景</h3>
                  <p className="text-sm text-foreground-600">{aboutData.vision}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Team */}
          <section className="py-10 md:py-12 border-t border-background-200/70">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                核心<span className="text-accent-500">团队</span>
              </h2>
              <p className="text-sm text-foreground-500">一群热爱电影的人，用心打造最好的观影平台</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutData.team.map((member) => (
                <div key={member.name} className="text-center">
                  <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-4 rounded-full overflow-hidden bg-background-100">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      title={`${member.name} - ${member.role}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900">{member.name}</h3>
                  <p className="text-xs text-foreground-500 mt-1">{member.role}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reviews */}
          <section className="py-10 md:py-12 border-t border-background-200/70">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                用户<span className="text-primary-500">口碑</span>
              </h2>
              <p className="text-sm text-foreground-500">来自真实用户的评价，是我们前进的动力</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {aboutData.reviews.map((review) => (
                <div
                  key={review.id}
                  className="p-5 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img
                        src={review.avatar}
                        alt={review.userName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground-900">{review.userName}</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${
                              i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                            }`}
                          ></i>
                        ))}
                      </div>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{review.date}</span>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="py-10 md:py-12 border-t border-background-200/70">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                常见<span className="text-accent-500">问题</span>
              </h2>
              <p className="text-sm text-foreground-500">关于68影院的常见疑问，这里都有答案</p>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-col gap-3">
                {aboutData.faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="py-10 md:py-12 border-t border-background-200/70">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                联系<span className="text-primary-500">我们</span>
              </h2>
              <p className="text-sm text-foreground-500">有任何问题或建议，欢迎随时联系我们</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">官方微信</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-5">
                  发送<span className="text-primary-500">消息</span>
                </h3>
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        消息内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 resize-none"
                        placeholder="请输入您的消息内容（最多500字）"
                      ></textarea>
                    </div>
                    <div className="anti-spam-field">
                      <label htmlFor="contact-phone-alt">备用电话</label>
                      <input
                        id="contact-phone-alt"
                        name="phone_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {submitting ? '发送中...' : '发送消息'}
                    </button>

                    {formStatus === 'success' && (
                      <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                        <i className="ri-check-line"></i>
                        消息已成功发送！我们会尽快与您联系。
                      </div>
                    )}
                    {formStatus === 'error' && formError && (
                      <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 p-3 rounded-lg">
                        <i className="ri-error-warning-line"></i>
                        {formError}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}